import { DateTime } from "luxon"
import queryString from "query-string"
import fp from "lodash/fp"

import type {
  IDateRange,
  IDateRangeISO,
  DateReference,
  DateRange,
} from "types/date.types"
import type { EntityType } from "types/common.types"

import { TOKEN_EXPIRATION_TIME } from "./constants"

/**
 * Dispatch an action to refresh de date range
 */
export default function refreshDateRange(
  dateReference: DateReference,
  dispatchDateRange: any,
): void {
  const dateNow = DateTime.now()
  dispatchDateRange({
    type: "UPDATE_DATE_RANGE",
    payload: {
      to_date: dateNow,
      from_date: dateNow.minus({
        [dateReference.timeUnit]: dateReference.timeAmount,
      }),
    },
  })
}

/**
 * Function that returns the datetime formatted
 *
 * @returns {string} the date formatted using "yyyy-MM-dd, HH:mm"
 */
export const formatDateTime = (datetime: DateTime | undefined): string =>
  datetime !== undefined ? datetime.toFormat("yyyy-MM-dd HH:mm") : "-"

/**
 * Function that returns the locale timestamp formatted
 *
 * @returns {string} the date formatted using "yyyy-MM-dd, HH:mm"
 */
export const formatTimestamp = (timestamp: string) => {
  return DateTime.fromISO(timestamp).toFormat("yyyy-MM-dd, HH:mm")
}

/**
 * Function that returns the date with a new format
 *
 * @param date number of **milliseconds** (uses the default zone)
 * @returns {string} the date is parsed to the format "yyyy-MM-dd, HH:mm"
 */
export const formatDateFromMillis = (date: number | undefined): string =>
  formatDateTime(date !== undefined ? DateTime.fromMillis(date) : undefined)

export const formatDateFromSeconds = (date: number | undefined): string =>
  formatDateTime(date !== undefined ? DateTime.fromSeconds(date) : undefined)

export const formatDateFromISO = (date: string | null | undefined): string =>
  formatDateTime(!fp.isNil(date) ? DateTime.fromISO(date) : undefined)

/**
 * Function that returns the stored day, without a time and without moving it to
 * the reader's zone. For a date that stands for a day rather than an instant,
 * such as when a power line was built.
 *
 * @returns {string} the date formatted using "yyyy-MM-dd"
 */
export const formatDayFromISO = (date: string | null | undefined): string =>
  !fp.isNil(date) ? DateTime.fromISO(date, { zone: "utc" }).toFormat("yyyy-MM-dd") : "-"

interface RelativeTimeOptions {
  now: DateTime
  locale: string | undefined
  aMomentAgo: string
  short?: boolean
}

/**
 * Function that returns how long ago the date was, in the locale's words
 *
 * @returns {string} such as "2 minutes ago", or `aMomentAgo` for the last minute and
 * for a date ahead of `now`
 */
export const formatRelativeTime = (
  date: DateTime,
  { now, locale, aMomentAgo, short = false }: RelativeTimeOptions,
): string => {
  if (now.diff(date).as("minutes") < 1) {
    return aMomentAgo
  }
  return (
    date.toRelative({
      base: now,
      locale: locale ?? "en",
      style: short ? "short" : "long",
      unit: ["years", "months", "weeks", "days", "hours", "minutes"],
    }) ?? "-"
  )
}

/**
 * Function that returns the date with a new format.
 *
 * @param {IDateRange} dateRange
 * @returns {IDateRangeISO}
 */
export const formatDateTimeToISO = (dateRange: IDateRange): IDateRangeISO => {
  return {
    fromDate: dateRange.fromDate?.toISO(),
    toDate: dateRange.toDate?.toISO(),
  }
}

export const formatSearchParams = (dateRange: DateRange | null): string => {
  if (!dateRange) return ""

  const isCustom = dateRange.range === "custom"
  const isZoom = dateRange.zoom

  // Custom: include range, fromDate, toDate (zoom is always false for custom)
  if (isCustom) {
    return queryString.stringify({
      range: dateRange.range,
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
    })
  }

  // Zoom mode (non-custom): include range, fromDate, toDate, zoom
  if (isZoom) {
    return queryString.stringify({
      range: dateRange.range,
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
      zoom: "true",
    })
  }

  // Normal range: only include range
  return queryString.stringify({ range: dateRange.range })
}

export const isWithinOneDay = (dateRange: DateRange): boolean => {
  const from = DateTime.fromISO(dateRange.fromDate)
  const to = DateTime.fromISO(dateRange.toDate)
  return to.diff(from).as("hours") <= 24
}

export const formatISOToMillis = (date: string): number =>
  DateTime.fromISO(date).toMillis()

export const formatMillisToDateTime = (date: number): DateTime =>
  DateTime.fromMillis(date)

export const formatISOToDateTime = (date: string): DateTime => DateTime.fromISO(date)

export const formatFilenameDate = (date: string): string =>
  DateTime.fromISO(date).toFormat("yyyy-MM-dd'T'HH-mm")

export const getNewTokenExpirationAt = (): string =>
  DateTime.now().plus({ minutes: TOKEN_EXPIRATION_TIME }).toISO()

export const formatReleaseDate = (ISODate: string, lang: string): string => {
  const releaseDate = DateTime.fromISO(ISODate)
  const format = lang === "es" ? "dd 'de' MMMM, yyyy" : "MMMM dd, yyyy"
  return releaseDate.setLocale(lang || "en").toFormat(format)
}

/**
 * Entities with a forecast accept dates ahead of the present; every other one is
 * capped at now.
 */
export const hasForecast = (entity: EntityType): boolean =>
  entity === "powerline" || entity === "span"

export const getForecastThresholdDate = (
  dateRange: DateRange | null,
): DateTime | null => {
  if (!dateRange || !dateRange.forecastThresholdDate) {
    return null
  }
  return DateTime.fromISO(dateRange.forecastThresholdDate)
}
